import { RootState, ThunkDispatch } from "core-integration/src/store";
import {
  cancelProposedChip,
  proposeAvailableChip,
} from "core-integration/src/store/chips/actions";
import {
  getActiveChipName,
  getAvailableChipNames,
  getPotentialChips,
  getProposedChipName,
} from "core-integration/src/store/chips/reducers";
import { IPotentialChip } from "core-integration/src/store/chips/types";
import { getElementTypesBySquadPosition } from "core-integration/src/store/element-types/reducers";
import { IElementTypesBySquadPosition } from "core-integration/src/store/element-types/types";
import {
  canAutocomplete,
  canReset,
  getErrors,
  getFreeTransfersRemaining,
  getProposedElements,
  getToSpend,
  getTransferCosts,
} from "core-integration/src/store/squad/reducers";
import { autoComplete, reset } from "core-integration/src/store/squad/thunks";
import {
  IProposedElements,
  ISquadErrors,
} from "core-integration/src/store/squad/types";
import { integerToMoney } from "core-integration/src/utils/money";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import styled from "styled-components";
import { ChipName, getChipName } from "../../utils/chips";
import Alert from "../Alert";
import Button from "../Button";
import Dialog from "../Dialog";
import DialogHeading from "../DialogHeading";
import DialogManager from "../DialogManager";
import { ScoreboardPanel } from "./ScoreboardPanel";

const FlexContainer = styled.div`
  display: flex;
`;

const ChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.space[2]};
  margin: ${({ theme }) => theme.space[4]} 0;
  padding: 0 ${({ theme }) => theme.space[2]};

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    padding: 0;
  }
`;

const ScoreBoardContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.space[2]};
  margin-bottom: ${({ theme }) => theme.space[2]};
  padding: 0 ${({ theme }) => theme.space[2]};

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    padding: 0;
  }
`;

const AutopickButtonsContainer = styled.div`
  display: flex;
`;

const ButtonWrap = styled.div`
  flex: 1;
  padding-left: 5%;
  padding-right: 5%;
  padding-top: ${({ theme }) => theme.space[4]};
  padding-bottom: ${({ theme }) => theme.space[4]};
`;

const PlayersSelectedWrap = styled.div`
  flex: 1;
  margin-right: 5%;
  padding-left: 5%;
`;

const MoneyRemainingWrap = styled.div`
  flex: 1;
  margin-left: 5%;
  padding-right: 5%;
`;

const CostScoreboardUnit = styled.div`
  flex: 1 0 33%;

  @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    flex: 1;
  }
`;

interface IBasicProps {
  autoPick: () => void;
  canAutocomplete: boolean;
  canReset: boolean;
  currencyDivisor: number;
  elementTypesByPosition: IElementTypesBySquadPosition;
  isBudgetExceeded: boolean;
  isNeedElements: boolean;
  proposedElements: IProposedElements;
  reset: () => void;
  toSpend: number;
}

const Basic: React.FC<IBasicProps> = ({
  autoPick,
  canAutocomplete,
  canReset,
  currencyDivisor,
  elementTypesByPosition,
  isBudgetExceeded,
  isNeedElements,
  proposedElements,
  reset,
  toSpend,
}) => {
  const { t } = useTranslation();
  return (
    <div>
      <AutopickButtonsContainer>
        <ButtonWrap>
          <Button
            variant="secondary"
            disabled={!canAutocomplete}
            onClick={autoPick}
            width={1}
          >
            {t("scoreboard.autoPick", "Auto Pick")}
          </Button>
        </ButtonWrap>
        <ButtonWrap>
          <Button
            variant="secondary"
            disabled={!canReset}
            onClick={reset}
            type="reset"
            width={1}
          >
            {t("reset", "Reset")}
          </Button>
        </ButtonWrap>
      </AutopickButtonsContainer>
      <FlexContainer>
        {/* <Box flex={1}> */}
        <PlayersSelectedWrap>
          <ScoreboardPanel
            heading={t("scoreboard.playersSelected", "Players Selected")}
            value={`${Object.keys(proposedElements).length} / ${
              Object.keys(elementTypesByPosition).length
            }`}
            isError={isNeedElements}
            isSuccess={!isNeedElements}
          />
        </PlayersSelectedWrap>
        {/* </Box> */}
        {/* <Box flex={1}> */}
        <MoneyRemainingWrap>
          <ScoreboardPanel
            heading={t("scoreboard.moneyRemaining", "Money Remaining")}
            value={`${integerToMoney(toSpend, currencyDivisor)}`}
            isError={isBudgetExceeded}
            isSuccess={!isBudgetExceeded}
          />
        </MoneyRemainingWrap>
        {/* </Box> */}
      </FlexContainer>
    </div>
  );
};

interface IExplainChipDialogProps {
  chip: string;
  handleHide: () => void;
  transferCosts: number;
}

const ExplainChipDialog: React.FC<IExplainChipDialogProps> = ({
  chip,
  handleHide,
  transferCosts,
}) => {
  const { t } = useTranslation();
  return (
    <Dialog closeDialog={handleHide}>
      <Dialog.Header closeDialog={handleHide}>
        <DialogHeading>
          {chip === "wildcard" && t("squad.playWildcardText", "Play Wildcard")}
          {chip === "uteam" && t("squad.playUnlimitText", "Play Loan Rangers")}
        </DialogHeading>
      </Dialog.Header>
      <Dialog.Body isPadded={true}>
        <Alert type="info">
          {transferCosts ? (
            <>
              <p>
                {chip === "wildcard" &&
                  t(
                    "squad.wildcardText1",
                    "You are now proposing to play your wildcard. Unlimited transfers can be made this Round without points deduction.",
                  )}
                {chip === "uteam" &&
                  t(
                    "squad.unlimitText1",
                    "You are now proposing to play your Loan Rangers chip. Unlimited transfers with as many players you want from a specific team can be picked this Round without points deduction.",
                  )}
              </p>
              <p>
                {chip === "wildcard" &&
                  t(
                    "squad.wildcardText2",
                    "Your wildcard will not be activated until you confirm your transfers.",
                  )}
                {chip === "uteam" &&
                  t(
                    "squad.unlimitText2",
                    "Your Loan Rangers chip will not be activated until you confirm your transfers",
                  )}
              </p>
            </>
          ) : (
            <p>
              {chip === "wildcard" &&
                t(
                  "squad.wildcardText3",
                  "To activate your wildcard you must be making enough proposed transfers that points will be deducted.",
                )}
              {chip === "uteam" &&
                t(
                  "squad.unlimitText3",
                  "To activate your Loan Rangers chip you must be making enough proposed transfers that points will be deducted.",
                )}
            </p>
          )}
        </Alert>
      </Dialog.Body>
    </Dialog>
  );
};

interface ICostProps {
  activeChipName: string;
  availableChipNames: string[];
  autoPick: () => void;
  canAutocomplete: boolean;
  canReset: boolean;
  currencyDivisor: number;
  freeTransfersRemaining: number;
  isBudgetExceeded: boolean;
  proposedChipName: string;
  reset: () => void;
  toSpend: number;
  transferCosts: number;
  cancelChip: (chip: string) => void;
  proposeChip: (chip: string) => void;
  chips: IPotentialChip[];
}

const Cost: React.FC<ICostProps> = ({
  activeChipName,
  autoPick,
  canAutocomplete,
  canReset,
  currencyDivisor,
  freeTransfersRemaining,
  isBudgetExceeded,
  proposedChipName,
  reset,
  toSpend,
  transferCosts,
  cancelChip,
  proposeChip,
  chips,
}) => {
  const { t } = useTranslation();
  const unlimitedChip = chips.filter((c) => c.name === "uteam")[0];
  const wildCardChip = chips.filter((c) => c.name === "wildcard")[0];
  return (
    <>
      <ChipsContainer>
        <CostScoreboardUnit>
          <Button
            disabled={!canAutocomplete}
            onClick={autoPick}
            width={1}
            variant="secondary"
          >
            {t("scoreboard.cost.autoPick", "Auto Pick")}
          </Button>
        </CostScoreboardUnit>
        <CostScoreboardUnit>
          <Button
            variant="secondary"
            disabled={!canReset}
            onClick={reset}
            type="reset"
            width={1}
          >
            {t("scoreboard.cost.reset", "Reset")}
          </Button>
        </CostScoreboardUnit>

        {activeChipName ? (
          <CostScoreboardUnit>
            <ScoreboardPanel
              heading={t("scoreboard.chip.active", "Active Chip")}
              value={getChipName(activeChipName as ChipName, t)}
            />
          </CostScoreboardUnit>
        ) : wildCardChip.status_for_entry === "played" ? (
          <CostScoreboardUnit>
            <ScoreboardPanel
              heading={t("scoreboard.wildcard.wildcard", "Wildcard")}
              value={t("scoreboard.wildcard.played", "Played")}
            />
          </CostScoreboardUnit>
        ) : wildCardChip.status_for_entry === "active" ? (
          <CostScoreboardUnit>
            <ScoreboardPanel
              heading={t("scoreboard.wildcard.wildcard", "Wildcard")}
              value={t("scoreboard.wildcard.active", "Active")}
            />
          </CostScoreboardUnit>
        ) : proposedChipName === "wildcard" ? (
          <CostScoreboardUnit>
            <Button width={1} onClick={() => cancelChip("wildcard")}>
              {t("squad.cancelWildcardText", "Cancel Wildcard")}
            </Button>
          </CostScoreboardUnit>
        ) : (
          <DialogManager
            render={(showDialog, handleShow, handleHide) => (
              <>
                <CostScoreboardUnit>
                  <Button
                    variant="primary"
                    disabled={
                      proposedChipName === "uteam" || activeChipName === "uteam"
                    }
                    width={1}
                    onClick={handleShow}
                  >
                    {t("squad.playWildcardTest", "Play Wildcard")}
                  </Button>
                </CostScoreboardUnit>
                {showDialog && (
                  <ExplainChipDialog
                    chip="wildcard"
                    handleHide={() => {
                      handleHide();
                      if (transferCosts) {
                        proposeChip("wildcard");
                      }
                    }}
                    transferCosts={transferCosts}
                  />
                )}
              </>
            )}
          />
        )}

        {activeChipName ? (
          <>&nbsp;</>
        ) : unlimitedChip.status_for_entry === "played" ? (
          <CostScoreboardUnit>
            <ScoreboardPanel
              heading={t("scoreboard.loanrangers", "Loan Rangers")}
              value={t("scoreboard.uteam.played", "Played")}
            />
          </CostScoreboardUnit>
        ) : unlimitedChip.status_for_entry === "active" ? (
          <CostScoreboardUnit>
            <ScoreboardPanel
              heading={t("scoreboard.loanrangers", "Loan Rangers")}
              value={t("scoreboard.uteam.active", "Active")}
            />
          </CostScoreboardUnit>
        ) : proposedChipName === "uteam" ? (
          <CostScoreboardUnit>
            <Button width={1} onClick={() => cancelChip("uteam")}>
              {t("squad.cancelUnlimitText", "Cancel Loan Rangers")}
            </Button>
          </CostScoreboardUnit>
        ) : (
          <DialogManager
            render={(showDialog, handleShow, handleHide) => (
              <>
                <CostScoreboardUnit>
                  <Button
                    variant="primary"
                    disabled={
                      proposedChipName === "wildcard" ||
                      activeChipName === "wildcard"
                    }
                    width={1}
                    onClick={handleShow}
                  >
                    {t("squad.playUnlimitText", "Play Loan Rangers")}
                  </Button>
                </CostScoreboardUnit>
                {showDialog && (
                  <ExplainChipDialog
                    chip="uteam"
                    handleHide={() => {
                      handleHide();
                      if (transferCosts) {
                        proposeChip("uteam");
                      }
                    }}
                    transferCosts={transferCosts}
                  />
                )}
              </>
            )}
          />
        )}
      </ChipsContainer>
      <ScoreBoardContainer>
        <CostScoreboardUnit>
          <ScoreboardPanel
            heading={t("squad.freeTransfers", "Free Transfers")}
            value={
              activeChipName === "wildcard" || activeChipName === "uteam"
                ? t("squad.unlimited", "Unlimited")
                : `${freeTransfersRemaining}`
            }
          />
        </CostScoreboardUnit>
        <CostScoreboardUnit>
          <ScoreboardPanel
            heading={t("squad.cost", "Cost")}
            value={`${transferCosts} pts`}
            isError={transferCosts > 0}
          />
        </CostScoreboardUnit>
        <CostScoreboardUnit>
          <ScoreboardPanel
            heading={t("squad.moneyRemaining", "Money Remaining")}
            value={`${integerToMoney(toSpend, currencyDivisor)}`}
            isError={isBudgetExceeded}
            isSuccess={!isBudgetExceeded}
          />
        </CostScoreboardUnit>
      </ScoreBoardContainer>
    </>
  );
};

interface IOwnProps {
  scoreboard: string;
}

interface IPropsFromState {
  activeChipName: string;
  availableChipNames: string[];
  canAutocomplete: boolean;
  canReset: boolean;
  currencyDivisor: number;
  elementTypesByPosition: IElementTypesBySquadPosition | null;
  errors: ISquadErrors;
  freeTransfersRemaining: number;
  proposedChipName: string;
  proposedElements: IProposedElements;
  toSpend: number;
  transferCosts: number;
  chips: IPotentialChip[];
}

interface IPropsFromDispatch {
  autoPick: () => void;
  reset: () => void;
  cancelChip: (chip: string) => void;
  proposeChip: (chip: string) => void;
}

type Props = IOwnProps & IPropsFromState & IPropsFromDispatch;

class Scoreboard extends React.Component<Props> {
  public render() {
    const {
      elementTypesByPosition,
      errors,
      proposedElements,
      scoreboard,
      ...rest
    } = this.props;
    if (!elementTypesByPosition) {
      return null;
    }
    const isNeedElements = Boolean(errors.needElements);
    const isBudgetExceeded = Boolean(errors.budgetExceeded);
    switch (scoreboard) {
      case "basic":
        return (
          <Basic
            {...rest}
            elementTypesByPosition={elementTypesByPosition}
            isNeedElements={isNeedElements}
            isBudgetExceeded={isBudgetExceeded}
            proposedElements={proposedElements}
          />
        );
      case "cost":
        return <Cost {...rest} isBudgetExceeded={isBudgetExceeded} />;
      default:
        return null;
    }
  }
}

const mapStateToProps = (state: RootState): IPropsFromState => ({
  activeChipName: getActiveChipName(state),
  availableChipNames: getAvailableChipNames(state),
  canAutocomplete: canAutocomplete(state),
  canReset: canReset(state),
  currencyDivisor: 10,
  elementTypesByPosition: getElementTypesBySquadPosition(state),
  errors: getErrors(state),
  freeTransfersRemaining: getFreeTransfersRemaining(state),
  proposedChipName: getProposedChipName(state),
  proposedElements: getProposedElements(state),
  toSpend: getToSpend(state),
  transferCosts: getTransferCosts(state),
  chips: getPotentialChips(state),
});

const mapDispatchToProps = (dispatch: ThunkDispatch): IPropsFromDispatch => ({
  autoPick: () => {
    if (!dispatch(autoComplete())) {
      // We should do something :-)
      window.console.log("Failed to autocomplete");
    }
  },
  cancelChip: (chip: string) => dispatch(cancelProposedChip(chip)),
  proposeChip: (chip: string) => dispatch(proposeAvailableChip(chip)),
  reset: () => dispatch(reset()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Scoreboard);
